/**
 * BUILD-7488 — Colección temporal a partir de productos seleccionados.
 * Helpers puros: no mutan productos ni el store. El flujo Vuex
 * (prepareTemporalCollection / loadMultiplePrint / removeTemporalCollection)
 * se reutiliza tal cual.
 */

/**
 * @param {Object|null|undefined} collection
 * @returns {boolean}
 */
export function isTemporalCollection(collection) {
    return Boolean(collection && collection.temporal)
}

/**
 * BUILD-7627: al cancelar el wizard, print.vue puede haber borrado ya
 * la colección temporal del store. Hay que volver al listado aunque
 * `currentCollection` ya no exista.
 *
 * @param {{ temporalCollectionId?: *, selectedCollectionId?: *, currentCollection?: Object|null, collections?: Object }} params
 * @returns {boolean}
 */
export function shouldRestoreProductListAfterWizard(params) {
    const temporalCollectionId = params && params.temporalCollectionId
    const selectedCollectionId = params && params.selectedCollectionId
    const currentCollection = params && params.currentCollection
    const collections = (params && params.collections) || {}

    if (temporalCollectionId) {
        return true
    }
    if (isTemporalCollection(currentCollection)) {
        return true
    }
    if (selectedCollectionId !== -1 && selectedCollectionId !== false && selectedCollectionId != null && !currentCollection) {
        return true
    }
    return isTemporalCollection(collections[selectedCollectionId])
}

/**
 * BUILD-7627: el listado del wizard muestra todos los productos de la
 * colección. Los deseleccionados quedan visibles (unchecked); el contador
 * se calcula aparte con la selección.
 *
 * @param {Array|undefined|null} items
 * @returns {Array}
 */
export function getWizardCollectionDisplayStates(items) {
    return Array.isArray(items) ? items : []
}

/**
 * @param {Object|null|undefined} state
 * @returns {number}
 */
export function resolveStateQuantity(state) {
    const quantity = Number(state && state.quantity)
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1
}

/**
 * loadMultiplePrint concatena ids (`id + ''` / `id + '-1'`), así que quedan
 * string. `aStatesToPrint` y `this.label.id` conservan el tipo original.
 *
 * @param {*} a
 * @param {*} b
 * @returns {boolean}
 */
export function samePrintItemId(a, b) {
    if (a == null || b == null) {
        return false
    }
    return String(a) === String(b)
}

/**
 * loadMultiplePrint fuerza ids a string (`id + ''`). `includes` es estricto
 * y deja el producto “quitado” en la selección si el tipo no coincide.
 *
 * @param {Array<*>} selectedIds
 * @param {*} itemId
 * @returns {boolean}
 */
export function isPrintItemSelected(selectedIds, itemId) {
    if (!Array.isArray(selectedIds) || itemId == null) {
        return false
    }
    return selectedIds.some((id) => samePrintItemId(id, itemId))
}

/**
 * @param {Array<*>} selectedIds
 * @param {*} itemId
 * @returns {Array<*>}
 */
export function togglePrintItemSelection(selectedIds, itemId) {
    const list = Array.isArray(selectedIds) ? selectedIds.slice() : []
    const index = list.findIndex((id) => samePrintItemId(id, itemId))
    if (index === -1) {
        list.push(itemId)
        return list
    }
    list.splice(index, 1)
    return list
}

/**
 * BUILD-7627: re-seleccionar un producto desmarcado lo vuelve a incluir
 * en la colección del wizard. No quita ids ya presentes.
 *
 * @param {Array<*>} selectedIds
 * @param {*} itemId
 * @returns {Array<*>}
 */
export function ensurePrintItemSelected(selectedIds, itemId) {
    if (itemId == null || isPrintItemSelected(selectedIds, itemId)) {
        return Array.isArray(selectedIds) ? selectedIds.slice() : []
    }
    return togglePrintItemSelection(selectedIds, itemId)
}

/**
 * BUILD-7627: el re-seleccionado entra justo después del producto actual
 * para no interrumpir ni perder el progreso del wizard en curso.
 *
 * @param {Array<*>} selectedIds
 * @param {*} itemId
 * @param {*} currentId
 * @returns {Array<*>}
 */
export function insertPrintItemAfterCurrent(selectedIds, itemId, currentId) {
    const list = Array.isArray(selectedIds) ? selectedIds.filter((id) => !samePrintItemId(id, itemId)) : []
    if (itemId == null) {
        return list
    }
    const currentIndex = list.findIndex((id) => samePrintItemId(id, currentId))
    if (currentIndex === -1) {
        list.push(itemId)
        return list
    }
    list.splice(currentIndex + 1, 0, itemId)
    return list
}

/**
 * Orden del wizard = orden de `selectedIds`, no el del catálogo original.
 *
 * @param {Array<Object>|undefined|null} allItems
 * @param {Array<*>} selectedIds
 * @returns {Array<Object>}
 */
export function getSelectedPrintItemsInOrder(allItems, selectedIds) {
    if (!Array.isArray(allItems) || !Array.isArray(selectedIds)) {
        return []
    }
    return selectedIds
        .map((id) => allItems.find((item) => item && samePrintItemId(item.id, id)))
        .filter(Boolean)
}

/**
 * Copia profunda de los items de printAll. cancel/resetMultiplePrint
 * mutan las mismas referencias del store y el último timer se perdía.
 *
 * @param {Array<Object>|null|undefined} items
 * @returns {Array<Object>}
 */
export function snapshotPrintAllItems(items) {
    if (!Array.isArray(items) || items.length === 0) {
        return []
    }
    try {
        return JSON.parse(JSON.stringify(items))
    } catch (error) {
        return items.map((item) => (item && typeof item === 'object' ? { ...item } : item))
    }
}

/**
 * Resuelve el producto en el store (id string/number) para no perder states.
 *
 * @param {Function} getter
 * @param {Object} product
 * @returns {Object|null}
 */
export function resolveProductFromStore(getter, product) {
    if (!product || product.id == null) {
        return product || null
    }
    if (typeof getter !== 'function') {
        return product
    }

    return getter(product.id) || getter(String(product.id)) || getter(Number(product.id)) || product
}

/**
 * @param {Object|null|undefined} state
 * @param {string|number} fallbackId
 * @returns {string|number|null}
 */
export function resolveStateId(state, fallbackId) {
    if (state && state.id != null && state.id !== '') {
        return state.id
    }
    if (fallbackId != null && fallbackId !== '') {
        return fallbackId
    }
    return null
}

/**
 * El alta individual usa el producto de timers. La colección temporal
 * no debe sustituirlo por una etiqueta con el mismo id y sin plantilla:
 * sin `format` el wizard no genera pasos.
 *
 * @param {Object|null|undefined|false} labelProduct
 * @param {Object|null|undefined|false} timerProduct
 * @param {string|number|null|undefined} stateId
 * @param {string|null|undefined} productSource
 * @returns {Object|null}
 */
export function pickTemporalSourceProduct(labelProduct, timerProduct, stateId, productSource) {
    const labelState = labelProduct && labelProduct.states ? labelProduct.states[stateId] : null
    const timerState = timerProduct && timerProduct.states ? timerProduct.states[stateId] : null

    if (productSource === 'timerproduct' && timerState) {
        return timerProduct
    }
    if (labelState) {
        return labelProduct
    }
    if (timerState) {
        return timerProduct
    }
    return labelProduct || timerProduct || null
}

/**
 * @param {Object|null|undefined} state
 * @param {boolean} includeHidden
 * @returns {boolean}
 */
function isWizardStateUsable(state, includeHidden) {
    return Boolean(state) && (includeHidden || state.visible !== false)
}

/**
 * BUILD-7688: elige la key real de `product.states`.
 * `prepareTemporalCollection` indexa por esa key, no por `state.id`.
 *
 * @param {Object|null|undefined} product
 * @param {{ preferredStateId?: string|number|null, includeHidden?: boolean }} [options]
 * @returns {string|null}
 */
export function pickWizardStateKey(product, options = {}) {
    if (!product || !product.states || typeof product.states !== 'object') {
        return null
    }

    const entries = Object.entries(product.states)
    if (entries.length === 0) {
        return null
    }

    const includeHidden = Boolean(options.includeHidden)
    const preferredStateId = options.preferredStateId
    const hasPreferred = preferredStateId != null && preferredStateId !== ''

    if (hasPreferred) {
        const preferred = entries.find(([stateKey, state]) => {
            if (!isWizardStateUsable(state, includeHidden)) {
                return false
            }
            const stateId = resolveStateId(state, stateKey)
            return String(stateKey) === String(preferredStateId) || String(stateId) === String(preferredStateId)
        })
        if (preferred) {
            return preferred[0]
        }
    }

    const usable = entries.find(([, state]) => isWizardStateUsable(state, includeHidden))
    return usable ? usable[0] : null
}

/**
 * BUILD-7688: una referencia por producto seleccionado.
 * El filtro de listado (estado/categoría) no excluye productos.
 *
 * @param {Array<Object>} products
 * @param {{ preferredStateId?: string|number|null, includeHidden?: boolean }} [options]
 * @returns {Array<Object>}
 */
export function buildWizardProductReferences(products, options = {}) {
    const list = Array.isArray(products) ? products : []

    return list.reduce((references, product) => {
        if (!product || product.id == null || !product.states) {
            return references
        }

        let stateKey = pickWizardStateKey(product, options)
        if (stateKey == null && !options.includeHidden) {
            stateKey = pickWizardStateKey(product, {
                preferredStateId: options.preferredStateId,
                includeHidden: true
            })
        }
        if (stateKey == null) {
            return references
        }

        const state = product.states[stateKey]
        if (!state) {
            return references
        }

        references.push({
            id: product.id,
            state_id: stateKey,
            quantity: resolveStateQuantity(state),
            name: product.name,
            category_id: product.category_id,
            image: product.image,
            note: product.note
        })

        return references
    }, [])
}

/**
 * Arma `references[]` `{ id, state_id, quantity, ... }` para
 * `label/prepareTemporalCollection`.
 *
 * - Por defecto solo estados visibles (mismo criterio que el listado).
 * - Si hay filtro de estado activo, solo ese estado.
 * - El id de estado cae al key del mapa si el objeto no trae `id`.
 *
 * @param {Array<Object>} products
 * @param {{ filteredStateId?: string|number|null, includeHidden?: boolean }} [options]
 * @returns {Array<Object>}
 */
export function buildTemporalCollectionReferences(products, options = {}) {
    const list = Array.isArray(products) ? products : []
    const filteredStateId = options.filteredStateId

    return list.reduce((references, product) => {
        if (!product || product.id == null || !product.states) {
            return references
        }

        Object.entries(product.states).forEach(([stateKey, state]) => {
            if (!state) {
                return
            }
            if (!options.includeHidden && state.visible === false) {
                return
            }

            const stateId = resolveStateId(state, stateKey)
            if (stateId == null) {
                return
            }
            if (filteredStateId && String(stateId) !== String(filteredStateId)) {
                return
            }

            references.push({
                id: product.id,
                state_id: stateId,
                quantity: resolveStateQuantity(state),
                name: product.name,
                category_id: product.category_id,
                image: product.image,
                note: product.note
            })
        })

        return references
    }, [])
}

/**
 * Convierte la colección temporal al arreglo que espera el wizard de print
 * (`aStatesToPrint`). Usa la key real del estado en el store.
 *
 * @param {Object} collection
 * @returns {Array<Object>}
 */
export function buildPrintStatesFromCollection(collection) {
    if (!collection || !collection.references) {
        return []
    }

    const statesToPrint = []

    Object.values(collection.references).forEach((reference) => {
        if (!reference || !reference.states) {
            return
        }

        Object.entries(reference.states).forEach(([stateKey, state]) => {
            if (!state || state.selected === false) {
                return
            }

            const stateId = resolveStateId(state, stateKey)
            statesToPrint.push({
                id: reference.id,
                stateID: stateKey,
                collectionID: collection.id,
                referenceID: reference.id,
                name: reference.name,
                state: state.name,
                image: reference.image,
                quantity: resolveStateQuantity(state),
                category_id: reference.category_id,
                states: {
                    [stateId]: Object.assign({}, state, { id: stateId })
                },
                note: reference.note
            })
        })
    })

    return statesToPrint
}

/**
 * loadMultiplePrint indexa por `state.id`. Si falta, usa la key del mapa.
 *
 * @param {Object} collection
 * @returns {Object}
 */
export function ensureCollectionStateIds(collection) {
    if (!collection || !collection.references) {
        return collection
    }

    Object.values(collection.references).forEach((reference) => {
        if (!reference || !reference.states) {
            return
        }
        Object.entries(reference.states).forEach(([stateKey, state]) => {
            if (state && (state.id == null || state.id === '')) {
                state.id = stateKey
            }
        })
    })

    return collection
}

/**
 * Normaliza `fields` de loadMultiplePrint / printAll (objeto o array).
 *
 * @param {Object|Array|null|undefined} fields
 * @returns {Array<Object>}
 */
export function normalizePrintFields(fields) {
    if (Array.isArray(fields)) {
        return fields
    }
    if (fields && typeof fields === 'object') {
        return Object.values(fields)
    }
    return []
}

/**
 * Completitud de pasos del wizard (sin cachear `canPrint`).
 * quantity/state no bloquean; el resto exige result o optional.
 *
 * @param {Object|Array|null|undefined} fields
 * @returns {boolean}
 */
export function arePrintFieldsComplete(fields) {
    const steps = normalizePrintFields(fields)
    if (steps.length === 0) {
        return false
    }

    return steps.every((step) => {
        if (!step || ['quantity', 'state'].includes(step.keyname)) {
            return true
        }
        return (step.result && step.result != null && step.result != '') || step.optional
    })
}

let offlineTimerSeq = 0

/**
 * Id de timer y clave de cola offline únicos en el mismo milisegundo.
 * `moment().valueOf()` colisionaba en altas masivas.
 *
 * @returns {{ id: string, queueKey: string }}
 */
export function nextOfflineTimerIdentity() {
    offlineTimerSeq += 1
    const key = Date.now() + '_' + offlineTimerSeq
    return {
        id: 'tp_' + key,
        queueKey: String(key)
    }
}

/**
 * Mapea un item de `loadMultiplePrint` al payload que espera `timerPreparer`.
 * Usa `referenceID` para no enviar ids sufijados (`prod-1`) a `addTimer`.
 *
 * @param {Object} item
 * @param {Object} printer
 * @returns {Object}
 */
export function mapPrintAllItemToTimerPayload(item, printer) {
    const source = item && typeof item === 'object' ? item : {}
    const states = source.states && typeof source.states === 'object' ? source.states : {}
    const firstState = Object.values(states)[0] || {}
    const productId = source.referenceID || source.id
    const stateId = source.stateID || firstState.id
    const fields = normalizePrintFields(source.fields)

    return {
        data: [
            {
                keyname: 'state',
                result: firstState.name,
                selected: stateId
            },
            ...fields.filter((field) => field && !['quantity', 'state'].includes(field.keyname)),
            {
                keyname: 'quantity',
                result: source.quantity || 1
            }
        ],
        processData: {
            label: {
                ...source,
                id: productId
            },
            selectedState: {
                digital: firstState.digital,
                id: stateId,
                name: firstState.name
            }
        },
        autoDateCalc: printer && printer.autoDateCalc,
        bUpdatedPrintDate: printer && printer.bUpdatedPrintDate,
        digitalState: printer && printer.digitalState,
        oParams: (printer && printer.oParams) || {},
        productName: source.name,
        label: {
            note: source.note,
            note1: source.info && source.info.note1 ? source.info.note1 : '',
            note2: source.info && source.info.note2 ? source.info.note2 : '',
            note3: source.info && source.info.note3 ? source.info.note3 : ''
        }
    }
}
